/**
 * Pure event-list transform helpers.
 *
 * Extracted from App.tsx so the component stays under the file-length standard
 * and these transforms stay small and unit-testable.
 */
import { UnscheduledEvent } from '../types';
import { ErtsPredictionSummary } from '../services/ertsPredictionService';
import { formatProjectedErts } from '../components/eventModal/useProjectedErts';

/**
 * Orders events by carrier (ascending), then tail number (ascending).
 * Returns a new sorted array; does not mutate the input.
 */
export function byCarrierThenTail(events: UnscheduledEvent[]): UnscheduledEvent[] {
  return [...events].sort((a, b) => {
    const carrierCompare = (a.carrier || '').localeCompare(b.carrier || '');
    if (carrierCompare !== 0) return carrierCompare;
    return (a.tailNumber || '').localeCompare(b.tailNumber || '');
  });
}

/**
 * Overlays the ERTS Prediction Pipeline's projected return times onto each
 * event's `projectedErts` map. The pipeline scores each defect, so when a
 * per-defect breakdown is available each defect id gets its own projected value;
 * otherwise the event-level latest is applied to every defect (defaulting to
 * defect 1 when none are listed). The homepage column shows the latest across
 * defects. Events without a projection keep their existing (persisted) value.
 */
export function applyProjectedErts(
  events: UnscheduledEvent[],
  predictions: Record<string, ErtsPredictionSummary>,
): UnscheduledEvent[] {
  return events.map((event) => {
    const summary = predictions[event.eventId];
    if (!summary) return event;

    const projectedErts: Record<number, string> = {};
    const defectEntries = Object.entries(summary.defects || {});

    if (defectEntries.length > 0) {
      // Per-defect projections: apply each defect's own value.
      for (const [defectId, zulu] of defectEntries) {
        const value = formatProjectedErts(zulu);
        if (value) projectedErts[Number(defectId)] = value;
      }
    } else {
      // Fall back to the event-level latest across every defect id.
      const value = formatProjectedErts(summary.latest || '');
      if (!value) return { ...event, projectedErts };
      const defectIds =
        event.defects && event.defects.length > 0 ? event.defects.map((defect) => defect.id) : [1];
      for (const id of defectIds) {
        projectedErts[id] = value;
      }
    }
    return { ...event, projectedErts };
  });
}

/**
 * Reconciles a freshly-fetched event list against the one already in state so a
 * poll only triggers re-renders for rows that actually changed.
 *
 * Why: the homepage polls every ~2s. Calling setState with a brand-new array of
 * brand-new objects every tick makes React re-render (and visibly repaint) every
 * row even when nothing changed — the "flash" on each poll. This helper:
 *   - returns the PREVIOUS array reference when the next list is deeply equal
 *     (the common no-change poll), so React bails out of the render entirely; and
 *   - otherwise returns a new array that REUSES each unchanged row's previous
 *     object reference, so only genuinely-changed rows re-render.
 *
 * Equality is a stable-key JSON comparison per row, which is sufficient for the
 * list-projection shape (flat fields + small projectedErts/defects arrays).
 */
function rowsEqual(a: UnscheduledEvent, b: UnscheduledEvent): boolean {
  return stableStringify(a) === stableStringify(b);
}

/** Deterministic JSON: object keys sorted so key order never affects equality. */
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, k) => {
          acc[k] = (val as Record<string, unknown>)[k];
          return acc;
        }, {});
    }
    return val;
  });
}

export function mergeEvents(
  prev: UnscheduledEvent[],
  next: UnscheduledEvent[],
): UnscheduledEvent[] {
  const prevById = new Map(prev.map((e) => [e.eventId, e]));
  let changed = next.length !== prev.length;

  const merged = next.map((nextEvent) => {
    const prevEvent = prevById.get(nextEvent.eventId);
    if (prevEvent && rowsEqual(prevEvent, nextEvent)) {
      return prevEvent; // reuse the old reference → this row won't re-render
    }
    changed = true;
    return nextEvent;
  });

  // Whole list unchanged (same ids, same order, same contents) → keep the old
  // array reference so React skips the render entirely.
  return changed ? merged : prev;
}
